import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import API_URL from "../config/apiConfig";
import { DEFAULT_MENU_THEME } from "../config/menuThemes";
import { formatPersianPrice, getCartSummary, parseTableNumber } from "../services/customerMenuModel";
import { parsePersianNumber } from "../utils/persianNumber";

const readCart = (menuLink) => {
  try {
    const value = JSON.parse(localStorage.getItem(`cart_${menuLink}`));
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
};

const useCustomerCart = () => {
  const { menuLink } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [restaurantData, setRestaurantData] = useState(null);
  const [theme, setTheme] = useState(() => localStorage.getItem(`theme_${menuLink}`) || DEFAULT_MENU_THEME);
  const [cart, setCart] = useState(() => readCart(menuLink));
  const [tableNumber, setTableNumber] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const summary = useMemo(() => getCartSummary(cart), [cart]);
  const queryTable = useMemo(() => parseTableNumber(location.search), [location.search]);
  const tableSuppliedByQr = queryTable !== null;

  useEffect(() => {
    const storedTable = localStorage.getItem(`tableNumber_${menuLink}`) || localStorage.getItem("tableNumber");
    setTableNumber(String(queryTable || parsePersianNumber(storedTable) || ""));
    if (queryTable) localStorage.setItem(`tableNumber_${menuLink}`, String(queryTable));
    setCart(readCart(menuLink));

    const controller = new AbortController();
    fetch(`${API_URL}/api/menus/public/${encodeURIComponent(menuLink)}`, { signal: controller.signal })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || "دریافت اطلاعات رستوران ناموفق بود.");
        setRestaurantData(data.restaurant);
        setTheme(data.theme || DEFAULT_MENU_THEME);
        localStorage.setItem(`theme_${menuLink}`, data.theme || DEFAULT_MENU_THEME);
      })
      .catch((requestError) => {
        if (requestError.name !== "AbortError") setError(requestError.message || "دریافت اطلاعات رستوران ناموفق بود.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [location.search, menuLink, queryTable]);

  const updateCart = (nextCart) => {
    setCart(nextCart);
    localStorage.setItem(`cart_${menuLink}`, JSON.stringify(nextCart));
  };

  const changeQuantity = (item, delta) => {
    const updated = cart.map((cartItem) => ({ ...cartItem }));
    const target = updated.find((cartItem) => cartItem._id === item._id);
    if (!target) return;
    target.quantity += delta;
    updateCart(updated.filter((cartItem) => cartItem.quantity > 0));
  };

  const removeItem = (item) => updateCart(cart.filter((cartItem) => cartItem._id !== item._id));

  const validateCheckout = () => {
    const parsedTable = parsePersianNumber(tableNumber);
    if (!Number.isInteger(parsedTable) || parsedTable <= 0) {
      setError("شماره میز را به‌صورت یک عدد معتبر وارد کنید.");
      return null;
    }
    setError("");
    localStorage.setItem(`tableNumber_${menuLink}`, String(parsedTable));
    return parsedTable;
  };

  const submitOrder = async () => {
    const parsedTable = validateCheckout();
    if (!parsedTable || !restaurantData?._id || !cart.length || submitting) return false;
    setSubmitting(true);
    setError("");
    try {
      const response = await fetch(`${API_URL}/api/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          restaurant: restaurantData._id,
          tableNumber: parsedTable,
          items: cart.map((item) => ({
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            total: item.price * item.quantity,
            image: item.image,
          })),
          total: summary.total,
          paymentType: "cash",
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || data.error || "ثبت سفارش ناموفق بود.");
      localStorage.setItem("lastOrder", JSON.stringify({ ...data.order, menuLink }));
      localStorage.removeItem(`cart_${menuLink}`);
      localStorage.removeItem(`tableNumber_${menuLink}`);
      localStorage.removeItem("tableNumber");
      setCart([]);
      navigate(`/menu/${menuLink}/order-success`);
      return true;
    } catch (submitError) {
      setError(submitError.message || "ثبت سفارش ناموفق بود.");
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  return {
    menuLink,
    restaurantData,
    theme,
    cart,
    tableNumber,
    setTableNumber,
    queryTable,
    tableSuppliedByQr,
    loading,
    submitting,
    error,
    setError,
    summary,
    changeQuantity,
    removeItem,
    validateCheckout,
    submitOrder,
    goToMenu: () => navigate(`/menu/${menuLink}${location.search}`),
    formatPersianPrice,
  };
};

export default useCustomerCart;
